import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMonthlyIncome,
  fetchMonthlyDropouts,
  calculateNetRevenue,
  addExpense,
  createNewExpense,
  calculateNetIncome,
  updateExpenses,
  deleteExpense,
  fetchExpenses,
  calculateTotalActualRevenue,
  fetchClinicSalesRevenue,
} from "../redux/reducers/profitLossSlice";
import MainLayout from "../layout/MainLayout";
import Header from "../components/Header";
import { formatCurrency, generateYearOptions, months } from "../utils/utils";
import { BiPencil, BiTrash } from "react-icons/bi";
import Button from "../components/Button";
import ConfirmDialog from "../components/confirm-alert/ConfirmDialog ";
import Loader from "../components/loader/loader";
import UnPaidCheckout from "./UnPaidCheckout";
import { getUserYears } from "../redux/reducers/userSlice";

// Add this helper function at the top of the file, outside the component

const ProfitLoss = ({ unPaid }) => {
  const dispatch = useDispatch();
  const {
    monthlyIncome,
    monthlyDropouts,
    netRevenue,
    expenses,
    netIncome,
    loading,
    clinicSalesRevenue,
    totalActualRevenue
  } = useSelector((state) => state.profitLoss);

  const { programs } = useSelector(
    (state) => state.programs ?? []
  );

  const {systemYears} = useSelector((state) => state.users);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showModal, setShowModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showAddExpenseConfirm, setShowAddExpenseConfirm] = useState(false);
  const { userDetails } = useSelector((state) => state.auth);
  const { userId } = userDetails;
  // const years = generateYearOptions();

  useEffect(() => {
    // Fetch both projected and actual income
    dispatch(
      fetchMonthlyIncome({ userId, year: selectedYear, type: "Projected" })
    );
    dispatch(
      fetchMonthlyIncome({ userId, year: selectedYear, type: "Actual" })
    );
    dispatch(fetchMonthlyDropouts({ userId, year: selectedYear }));
    dispatch(calculateNetRevenue({ userId, year: selectedYear }));
    dispatch(calculateNetIncome({ userId, year: selectedYear }));
    dispatch(fetchExpenses({ userId, year: selectedYear }));
    dispatch(calculateTotalActualRevenue({ userId, year: selectedYear }));
    dispatch(fetchClinicSalesRevenue({ userId, year: selectedYear }));
    dispatch(getUserYears( userId));


  }, [dispatch, selectedYear, userId]);



  useEffect(() => {
    // Fetch both projected and actual income
   setTimeout(() => {
    dispatch(
      fetchMonthlyIncome({ userId, year: selectedYear, type: "Projected" })
    );
    dispatch(
      fetchMonthlyIncome({ userId, year: selectedYear, type: "Actual" })
    );
    dispatch(fetchMonthlyDropouts({ userId, year: selectedYear }));
    dispatch(calculateNetRevenue({ userId, year: selectedYear }));
    dispatch(calculateNetIncome({ userId, year: selectedYear }));
    dispatch(fetchExpenses({ userId, year: selectedYear }));
    dispatch(calculateTotalActualRevenue({ userId, year: selectedYear }));
    dispatch(fetchClinicSalesRevenue({ userId, year: selectedYear }));
    dispatch(getUserYears( userId));
   }, 1000);


  }, []);

  const handleSave = async () => {
    setShowSaveConfirm(false);
    try {
      await Promise.all(
        expenses.map((expense) =>
          dispatch(
            addExpense({
              ...expense,
              userId,
              year: selectedYear,
            })
          ).unwrap()
        )
      );
      // Refresh the expenses list after saving
      await dispatch(fetchExpenses({ userId, year: selectedYear }));
      // Recalculate net income
      await dispatch(calculateNetIncome({ userId, year: selectedYear }));
    } catch (error) {
    }
  };

  const handleAddExpense = async () => {
    setShowAddExpenseConfirm(false);
    try {
      await dispatch(createNewExpense({ userId, year: selectedYear })).unwrap();
      await dispatch(fetchExpenses({ userId, year: selectedYear }));
    } catch (error) {
    }
  };

  const handleDeleteExpense = async () => {
    const expenseToBeDeleted = expenses[expenseToDelete];

    if (expenseToBeDeleted) {
      try {
        await dispatch(
          deleteExpense({
            userId,
            expenseId: expenseToBeDeleted._id,
          })
        ).unwrap();

        // Refresh expenses list after successful deletion
        await dispatch(fetchExpenses({ userId, year: selectedYear }));
        // Recalculate net income after deleting expense
        await dispatch(calculateNetIncome({ userId, year: selectedYear }));

        setShowConfirmDialog(false);
        setExpenseToDelete(null);
      } catch (error) {
      }
    }
  };

  // Helper function to format month data to lowercase keys
  const formatMonthData = (data) => {
    const formattedData = {};
    if (!data) return formattedData;

    Object.entries(data).forEach(([key, value]) => {
      formattedData[key.toLowerCase()] = value;
    });
    return formattedData;
  };

  // Transform the data for the revenue table
  const revenueData = [
    {
      name: "Projected Monthly Tuition Income",
      ...(monthlyIncome?.projected
        ? formatMonthData(monthlyIncome?.projected)
        : {}),
      total: monthlyIncome?.projected
        ? Object?.values(monthlyIncome?.projected).reduce(
            (total, value) => parseFloat(total) + (parseFloat(value) || 0),
            0
          )
        : 0,
    },
    {
      name: "Actual Monthly Tuition Income",
      ...(monthlyIncome?.actual ? formatMonthData(monthlyIncome?.actual) : {}),
      total: monthlyIncome?.actual
        ? Object.values(monthlyIncome?.actual).reduce(
            (total, value) => parseFloat(total) + (parseFloat(value) || 0),
            0
          )
        : 0,
    },
    {
      name: "Actual Dropout Students Amount",
      ...(monthlyDropouts ? formatMonthData(monthlyDropouts) : {}),
      total: monthlyDropouts
        ? Object.values(monthlyDropouts).reduce(
            (total, value) => parseFloat(total) + (parseFloat(value) || 0),
            0
          )
        : 0,
    },
    {
      name: "Actual Tuition Income Revenue",
      ...(netRevenue ? formatMonthData(netRevenue) : {}),
      total: netRevenue
        ? Object.values(netRevenue).reduce(
            (total, value) => parseFloat(total) + (parseFloat(value) || 0),
            0
          )
        : 0,
    },
    {
      name: " Actual Clinic Income Revenue ",
      ...(clinicSalesRevenue ? formatMonthData(clinicSalesRevenue) : {}),
      total: clinicSalesRevenue
        ? Object.values(clinicSalesRevenue).reduce(
            (total, value) => parseFloat(total) + (parseFloat(value) || 0),
            0
          )
        : 0,
    },
    {
      name: "Total Actual Revenue",
      ...(totalActualRevenue ? formatMonthData(totalActualRevenue) : {}),
      total: totalActualRevenue
        ? Object.values(totalActualRevenue).reduce(
            (total, value) => parseFloat(total) + (parseFloat(value) || 0),
            0
          )
        : 0,
    },
  ];

  // Format net income data for display
  const formattedNetIncome = netIncome ? formatMonthData(netIncome) : {};

  // const calculateTotalExpenses = (expenses) => {
  //   return expenses.reduce((total, expense) => {
  //     // Sum all months for the current expense
  //     const monthlyTotal = Object.entries(expense)
  //       .filter(([key]) =>
  //         ["january", "february", "march", "april", "may", "june",
  //          "july", "august", "september", "october", "november", "december"]
  //         .includes(key.toLowerCase()))
  //       .reduce((sum, [_, value]) => sum + (parseFloat(value) || 0), 0);

  //     // Add the monthly total to the overall total
  //     return formatCurrency(  total + monthlyTotal);
  //   }, 0); // Initial total is 0
  // };

  const calculateTotalExpenses = (expenses, index) => {
    if (index < 0 || index >= expenses.length) {
      return "Invalid index";
    }

    const expense = expenses[index];

    const total = Object.keys(expense)
      .filter(
        (key) =>
          key !== "_id" &&
          key !== "name" &&
          key !== "userId" &&
          key !== "year" &&
          key !== "__v"
      )
      .reduce((sum, key) => sum + (parseFloat(expense[key]) || 0), 0);

    return formatCurrency(total);
  };

  return (
    <MainLayout>
      <Loader loading={loading} />
      {unPaid ? (
        <UnPaidCheckout />
      ) : (
        <>
          <Header
            title={"Revenue Actual"}
            subTitle={`${programs.length} Program`}
            description={
              "Manage your Monthly Clinic Calculator and preferences here."
            }
            className="text-base sm:text-lg md:text-xl"
          >
            {/* <Button> */}
              <select
                className="bg-purple-600 hover:bg-purple-700 px-5 py-3 rounded-xl text-white"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {systemYears?.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            {/* </Button> */}
          </Header>

          <>
            {/* Revenue Table */}
            <div className="overflow-auto w-full">
              <table className="min-w-full bg-white border border-gray-200 text-xs sm:text-sm md:text-base">
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs sm:text-sm md:text-base font-bold text-gray-500 uppercase tracking-wider">
                      Name
                    </td>
                    {months.map((month, idx) => (
                      <th
                        key={idx}
                        className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs sm:text-sm md:text-base font-bold text-gray-500 uppercase tracking-wider"
                      >
                        {month}
                      </th>
                    ))}
                    <td className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs sm:text-sm md:text-base font-bold text-gray-500 uppercase tracking-wider">
                      Total
                    </td>
                  </tr>
                  {revenueData.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      <td className="px-6 py-4 whitespace-nowrap text-xs sm:text-sm md:text-base text-gray-900 border-b border-gray-200">
                        {row?.name}
                      </td>
                      {months.map((month) => (
                        <td
                          key={month}
                          className="px-6 py-4 whitespace-nowrap text-xs sm:text-sm md:text-base text-gray-900 border-b border-gray-200"
                        >
                          ${(parseFloat(row[month.toLowerCase()]) || 0).toFixed(2)}
                        </td>
                      ))}

                      <td>{formatCurrency((parseFloat(row?.total) || 0).toFixed(2))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Expenses Section */}
            <div className="mt-20">
              <Header
                title={"Expenses"}
                buttonText={"+ Add Expense"}
                btnOnlcick={() => setShowAddExpenseConfirm(true)}
                className="text-base sm:text-lg md:text-xl"
              >
                <Button btnOnlcick={() => setShowSaveConfirm(true)}>
                  Save
                </Button>
              </Header>
              <div className="overflow-auto w-full">
                <table className="min-w-full bg-white border border-gray-200 text-xs sm:text-sm md:text-base">
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs sm:text-sm md:text-base font-bold text-gray-500 uppercase tracking-wider">
                        Name
                      </td>
                      {months.map((month, idx) => (
                        <td
                          key={idx}
                          className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs sm:text-sm md:text-base font-bold text-gray-500 uppercase tracking-wider"
                        >
                          {month}
                        </td>
                      ))}
                      <td>Total</td>
                      <td className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs sm:text-sm md:text-base font-bold text-gray-500 uppercase tracking-wider">
                        Action
                      </td>
                    </tr>
                    {expenses?.map((expense, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-xs sm:text-sm md:text-base text-gray-900 border-b border-gray-200">
                          <div className="flex flex-row items-center">
                            <input
                              type="text"
                              value={expense.name || ""}
                              className="p-3 rounded-xl border-2 border-gray-500"
                              onChange={(e) => {
                                const updatedExpenses = expenses.map(
                                  (item, index) =>
                                    index === idx
                                      ? { ...item, name: e.target.value }
                                      : item
                                );
                                dispatch(updateExpenses(updatedExpenses));
                              }}
                            />
                            <BiPencil className="text-purple-500 text-lg ml-2" />
                          </div>
                        </td>
                        {months.map((month) => (
                          <td
                            key={month}
                            className="px-6 py-4 whitespace-nowrap text-xs sm:text-sm md:text-base text-gray-900 border-b border-gray-200"
                          >
                            <div className="flex flex-row items-center">
                              <input
                                type="number"
                                min={0}
                                value={(parseFloat(expense[month.toLowerCase()]) || 0).toFixed(2)}
                                className="p-3 rounded-xl border-2 border-gray-500"
                                onChange={(e) => {
                                  const updatedExpenses = expenses.map(
                                    (item, index) =>
                                      index === idx
                                        ? {
                                            ...item,
                                            [month.toLowerCase()]: Number(
                                              e.target.value
                                            ),
                                          }
                                        : item
                                  );
                                  dispatch(updateExpenses(updatedExpenses));
                                }}
                              />
                              <BiPencil className="text-purple-500 text-lg ml-2" />
                            </div>
                          </td>
                        ))}
                        <td>{calculateTotalExpenses(expenses, idx)}</td>

                        <td className="px-6 py-4 whitespace-nowrap text-xs sm:text-sm md:text-base text-gray-900 border-b border-gray-200">
                          <BiTrash
                            onClick={() => {
                              setShowConfirmDialog(true);
                              setExpenseToDelete(idx);
                            }}
                            className="text-3xl text-red-600 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Net Income Section */}
            <div className="mt-20">
              <Header title={"Net Income"} className="text-base sm:text-lg md:text-xl" />
              <div className="overflow-auto w-full">
                <table className="min-w-full bg-white border border-gray-200 text-xs sm:text-sm md:text-base">
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      {months.map((month, idx) => (
                        <th
                          key={idx}
                          className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs sm:text-sm md:text-base font-bold text-gray-500 uppercase tracking-wider"
                        >
                          {month}
                        </th>
                      ))}
                      <td className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs sm:text-sm md:text-base font-bold text-gray-500 uppercase tracking-wider">
                        Total
                      </td>
                    </tr>
                    <tr>
                      {months.map((month, idx) => (
                        <td
                          key={idx}
                          className="px-6 py-4 whitespace-nowrap text-xs sm:text-sm md:text-base text-gray-900 border-b border-gray-200"
                        >
                          $
                          {(
                            formattedNetIncome[month.toLowerCase()] || 0
                          ).toLocaleString()}
                        </td>
                      ))}
                      <td>
                        {netIncome
                          ? Object.values(netIncome).reduce(
                              (total, value) => parseFloat(parseFloat(total) + parseFloat(value)).toFixed(2),
                              0
                            )
                          : 0}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>

          {/* Existing modals */}
          <ConfirmDialog
            isOpen={showConfirmDialog}
            title="Delete Expense"
            message="Are you sure you want to delete this expense?"
            onConfirm={handleDeleteExpense}
            onCancel={() => {
              setShowConfirmDialog(false);
              setExpenseToDelete(null);
            }}
          />
          <ConfirmDialog
            isOpen={showSaveConfirm}
            title="Confirm Save"
            message="Are you sure you want to save all changes?"
            onConfirm={handleSave}
            onCancel={() => setShowSaveConfirm(false)}
          />
          <ConfirmDialog
            isOpen={showAddExpenseConfirm}
            title="Add New Expense"
            message="Do you want to add a new expense item?"
            onConfirm={handleAddExpense}
            onCancel={() => setShowAddExpenseConfirm(false)}
          />

         
        </>
      )}
    </MainLayout>
  );
};

export default ProfitLoss;
